import { Select, TextField } from '@cimpress/react-components';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { SelectorOption } from '../Components/common';
import { ApiKeyContext } from '../App';
import { CardBuilder, Control, Page, PreviewRow, PreviewRows, PreviewSection, SurfaceSelector } from '../Components';
import { ImageListEditor } from '../Components/CompositionCompose/images/ImageListEditor';
import type { ImageAsset } from '../Components/CompositionCompose/images/types';
import { StylizedTextListEditor } from '../Components/generateStylizedAsset/StylizedTextListEditor';
import type { TextAsset } from '../Components/CompositionCompose/texts/types';
import { getCompositionGenerateUrl } from '../api';
import presetSurfaces from '../data/generateSurfaceJson.json';
import presetSurfaceUrls from '../data/generateSurfaceUrls.json';

const STYLE_OPTIONS: SelectorOption[] = [
    { label: 'Watercolor', value: 'watercolor' },
    { label: 'Oil Painting', value: 'oil_painting' },
    { label: 'Single Lineart', value: 'single_lineart' },
    { label: 'Comic', value: 'comic' },
    { label: 'Pencil Sketch', value: 'pencil_sketch' },
    { label: 'Photomontage', value: 'photomontage' },
    { label: 'Pixel Art', value: 'pixel_art' },
    { label: 'Ink Sketch', value: 'ink_sketch' },
    { label: 'Crayon Drawing', value: 'crayon_drawing' },
    { label: 'Digital Art', value: 'digital_art' },
];

export const ComposeStylizedAssetsPage = () => {
    const [surfaceSpecificationUrl, setSurfaceSpecificationUrl] = useState('');
    const [surfaceSpecification, setSurfaceSpecification] = useState<any>(undefined);
    const [productName, setProductName] = useState('');
    const [images, setImages] = useState<ImageAsset[] | undefined>(undefined);
    const [texts, setTexts] = useState<TextAsset[] | undefined>(undefined);
    const [style, setStyle] = useState<SelectorOption>(STYLE_OPTIONS[0]);
    const [resultRow, setResultRow] = useState(PreviewRow());
    const apiKey = useContext(ApiKeyContext);

    const handleSetImages = useCallback((value?: ImageAsset[]) => {
        if (value) {
            const imagesWithUnits = value.map((img) => {
                const width = typeof img.width === 'number' ? `${img.width}px` : img.width;
                const height = typeof img.height === 'number' ? `${img.height}px` : img.height;

                // Only create new object if dimensions actually changed
                if (width === img.width && height === img.height) {
                    return img;
                }

                return {
                    ...img,
                    width,
                    height,
                };
            });
            setImages(imagesWithUnits);
        } else {
            setImages(undefined);
        }
    }, []);

    useEffect(() => {
        if ((surfaceSpecificationUrl || surfaceSpecification) && (images || texts)) {
            // Build assets object from inputs
            const assets: any = {};

            if (images && images.length > 0) {
                assets.images = images;
            }

            if (texts && texts.length > 0) {
                assets.texts = texts;
            }

            const resultCard = CardBuilder.ofType('documentUrl')
                .withTitle('Result Document')
                .withData({
                    documentUrl: getCompositionGenerateUrl({
                        assets,
                        surfaceSpecificationUrl,
                        surfaceSpecification,
                        productName,
                        version: 'v0',
                        style: style.value,
                        apiKey,
                    }),
                    backgroundColor: 'FFFFFF',
                })
                .build();
            setResultRow(PreviewRow(resultCard));
        } else {
            setResultRow(PreviewRow());
        }
    }, [surfaceSpecificationUrl, surfaceSpecification, productName, images, texts, style, apiKey]);

    return (
        <Page>
            <Page.Title>Composition Generate Stylized Assets</Page.Title>

            <Page.Controls>
                <Control>
                    <Control.Title>Style</Control.Title>
                    <Select label="style" value={style} onChange={setStyle} options={STYLE_OPTIONS} />
                </Control>

                <Control>
                    <Control.Title>Surface Selection</Control.Title>
                    <SurfaceSelector
                        surfaceSpecificationUrl={surfaceSpecificationUrl}
                        setSurfaceSpecificationUrl={setSurfaceSpecificationUrl}
                        surfaceSpecification={surfaceSpecification}
                        setSurfaceSpecification={setSurfaceSpecification}
                        presetSurfaceUrlsFromPage={presetSurfaceUrls}
                        presetSurfaceJsonFromPage={presetSurfaces}
                    />
                </Control>

                <Control>
                    <Control.Title>Product Name</Control.Title>
                    <TextField
                        label="Product Name"
                        onChange={(e) => setProductName(e.target.value)}
                        value={productName}
                    />
                </Control>

                <Control>
                    <Control.Title>Images</Control.Title>
                    <ImageListEditor initialValue={images} onChange={handleSetImages} />
                </Control>

                <Control>
                    <Control.Title>Texts</Control.Title>
                    <StylizedTextListEditor initialValue={texts} onChange={setTexts} />
                </Control>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction={
                        'Fill in a surface specification, style, and at least one asset (image or text) to generate a stylized design.'
                    }
                    showIntro={!(surfaceSpecificationUrl || surfaceSpecification) || !(images || texts)}
                    rows={PreviewRows(resultRow)}
                />
            </Page.Content>
        </Page>
    );
};
